import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ComponentType, ReactNode } from 'react';
import { Button, Drawer, Empty, Grid, Tooltip, Typography, theme, Layout } from 'antd';
import { DashboardOutlined, MenuFoldOutlined, MenuOutlined, MenuUnfoldOutlined, SearchOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';

import AdminLayout, { type AdminLayoutMobileMenuContext } from './AdminLayout.tsx';
import './EshopDashboardLayout.css';
import { useAppSelector } from '@store/store';
import { selectPlanCapabilities, selectSiteApps, selectSiteFeatureFlags } from '@store/slices/settingsSlice.tsx';
import {
    selectIsStaff,
    selectIsSuperuser,
    selectUserGroups,
    selectUserPermissions,
    selectUserRoles,
} from '@store/slices/userSlice.tsx';
import {
    DASHBOARD_NAV_ITEMS,
    findNavItemByPath,
    getVisibleNavItems,
} from '@features/dashboard/registry/dashboardNavRegistry.ts';
import type { DashboardNavItem, DashboardNavGroup } from '@features/dashboard/registry/types.ts';

const { Sider } = Layout;

type CSSVars = CSSProperties & Partial<Record<`--${string}`, string | number>>;

type EshopDashboardLayoutProps = {
    children?: ReactNode;
};

const DASHBOARD_ROUTE_KEY = 'dashboard.overview';
const ESHOP_HOME_PATH = '/eshop/dashboard';
const ESHOP_HOME_URL = '/eshop/dashboard';

/** Groups to show in the E-shop dashboard side panel */
const ESHOP_NAV_GROUPS: readonly DashboardNavGroup[] = ['eshop', 'general', 'cms'];

/** Group labels for side panel sections */
const GROUP_LABELS: Record<DashboardNavGroup, string> = {
    general: 'Configuración',
    eshop: 'E-Shop',
    cms: 'CMS',
    erp: 'ERP',
};

interface EshopSection {
    group: DashboardNavGroup;
    title: string;
    items: DashboardNavItem[];
}

/** Ant Design color palette - same as DashboardSections */
const ANT_COLORS: Record<string, string> = {
    'cyan-5': '#36cfc9',
    'cyan-6': '#13c2c2',
    'cyan-7': '#08979c',
    'cyan-8': '#006d75',
    'cyan-9': '#00474f',
    'blue-5': '#40a9ff',
    'blue-6': '#1890ff',
    'blue-7': '#096dd9',
    'orange-5': '#ffa940',
    'orange-6': '#fa8c16',
    'orange-7': '#d46b08',
    'green-5': '#73d13d',
    'green-6': '#52c41a',
    'green-7': '#389e0d',
    'purple-5': '#b37feb',
    'purple-6': '#722ed1',
    'purple-7': '#531dab',
    'magenta-5': '#ff85c0',
    'magenta-6': '#eb2f96',
    'magenta-7': '#c41d7f',
    'gold-5': '#ffc53d',
    'gold-6': '#faad14',
    'gold-7': '#d48806',
};

const resolveColorToken = (colorToken: string | undefined, fallback: string): string => {
    if (!colorToken) return fallback;
    return ANT_COLORS[colorToken] ?? fallback;
};

const EshopDashboardLayout: React.FC<EshopDashboardLayoutProps> = ({ children }) => {
    const { token } = theme.useToken();
    const screens = Grid.useBreakpoint();

    const isXL = !!screens.xl;
    const isMD = !!screens.md;
    const mode: 'mobile' | 'tablet' | 'desktop' = isXL ? 'desktop' : isMD ? 'tablet' : 'mobile';

    const siteApps = useAppSelector(selectSiteApps) ?? [];
    const features = useAppSelector(selectSiteFeatureFlags) ?? {};
    const planCapabilities = useAppSelector(selectPlanCapabilities);
    const isStaff = useAppSelector(selectIsStaff);
    const isSuperuser = useAppSelector(selectIsSuperuser);
    const permissions = useAppSelector(selectUserPermissions) ?? [];
    const groups = useAppSelector(selectUserGroups) ?? [];
    const roles = useAppSelector(selectUserRoles) ?? [];

    const [collapsed, setCollapsed] = useState(mode !== 'desktop');
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [tabletOverlayOpen, setTabletOverlayOpen] = useState(false);

    const visibleNavItems = useMemo(
        () =>
            getVisibleNavItems(DASHBOARD_NAV_ITEMS, {
                apps: siteApps,
                features,
                isStaff,
                isSuperuser,
                permissions,
                groups,
                roles,
                planCapabilities,
            }),
        [features, groups, isStaff, isSuperuser, permissions, planCapabilities, roles, siteApps],
    );

    const navItemMap = useMemo(
        () => new Map<string, DashboardNavItem>(visibleNavItems.map((item) => [item.key, item])),
        [visibleNavItems],
    );

    /** Build sections for the side panel - only eshop, general, cms groups */
    const sections = useMemo<EshopSection[]>(() => {
        const grouped = new Map<DashboardNavGroup, DashboardNavItem[]>();

        for (const item of visibleNavItems) {
            // Skip dashboard.overview as it will be rendered separately
            if (item.key === DASHBOARD_ROUTE_KEY) continue;
            // Only include allowed groups
            if (!ESHOP_NAV_GROUPS.includes(item.group)) continue;

            const items = grouped.get(item.group) ?? [];
            items.push(item);
            grouped.set(item.group, items);
        }

        return ESHOP_NAV_GROUPS
            .filter((group) => grouped.has(group))
            .map((group) => ({
                group,
                title: GROUP_LABELS[group],
                items: grouped.get(group) ?? [],
            }));
    }, [visibleNavItems]);

    useEffect(() => {
        if (mode === 'desktop') {
            setCollapsed(false);
            setMobileNavOpen(false);
            setTabletOverlayOpen(false);
        } else if (mode === 'tablet') {
            setCollapsed(true);
            setMobileNavOpen(false);
            setTabletOverlayOpen(false);
        } else {
            setMobileNavOpen(false);
            setTabletOverlayOpen(false);
        }
    }, [mode]);

    const navigate = useNavigate();
    const location = useLocation();

    const activeNavItem = useMemo(
        () => findNavItemByPath(visibleNavItems, location.pathname, location.search),
        [location.pathname, location.search, visibleNavItems],
    );

    const handleNavigate = useCallback(
        (to: string) => {
            navigate(to);
            if (mode === 'mobile') setMobileNavOpen(false);
            if (mode === 'tablet') setTabletOverlayOpen(false);
        },
        [mode, navigate],
    );

    const handleToggleCollapse = useCallback(() => {
        if (mode === 'tablet') {
            setTabletOverlayOpen((p) => !p);
            return;
        }
        if (mode === 'desktop') {
            setCollapsed((prev) => !prev);
        }
    }, [mode]);

    const handleOpenMobileNav = useCallback(() => setMobileNavOpen(true), []);
    const handleCloseMobileNav = useCallback(() => setMobileNavOpen(false), []);
    const handleOpenMobileSearch = useCallback((openCommandPalette: () => void) => {
        handleCloseMobileNav();

        if (typeof window === 'undefined') {
            openCommandPalette();
            return;
        }

        window.setTimeout(() => openCommandPalette(), 0);
    }, [handleCloseMobileNav]);

    const navIsCollapsed = mode === 'desktop' ? collapsed : mode === 'tablet' ? true : false;
    const hasSections = sections.length > 0;

    const getAccent = (item: DashboardNavItem) => {
        return resolveColorToken(item.colorToken, token.colorPrimary);
    };

    const renderNavButton = (
        title: string,
        description: string | undefined,
        isDisabled: boolean,
        isActive: boolean,
        accent: string,
        onClick: () => void,
        Icon?: ComponentType,
    ) => {
        const itemStyles: CSSVars = {
            '--eshop-nav-accent': accent,
        };

        const button = (
            <button
                type="button"
                onClick={onClick}
                className={[
                    'eshop-dashboard-layout__nav-item',
                    isActive ? 'eshop-dashboard-layout__nav-item--active' : '',
                    isDisabled ? 'eshop-dashboard-layout__nav-item--disabled' : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
                style={itemStyles}
                disabled={isDisabled}
                aria-current={isActive ? 'page' : undefined}
            >
                {Icon ? (
                    <span className="eshop-dashboard-layout__nav-icon" aria-hidden="true">
                        <Icon />
                    </span>
                ) : null}
                <span className="eshop-dashboard-layout__nav-text">
                    <span className="eshop-dashboard-layout__nav-label">{title}</span>
                    {description ? <span className="eshop-dashboard-layout__nav-description">{description}</span> : null}
                </span>
            </button>
        );

        if (navIsCollapsed) {
            return (
                <Tooltip
                    placement="right"
                    title={
                        <div className="eshop-dashboard-layout__tooltip-content">
                            <strong>{title}</strong>
                            {description ? <div>{description}</div> : null}
                        </div>
                    }
                >
                    <span className="eshop-dashboard-layout__tooltip-wrapper">{button}</span>
                </Tooltip>
            );
        }
        return button;
    };

    const renderNavSections = () => {
        if (!hasSections) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <div className="eshop-dashboard-layout__empty">
                            <Typography.Text type="secondary">No hay módulos disponibles para tu rol.</Typography.Text>
                        </div>
                    }
                />
            );
        }

        return (
            <nav className="eshop-dashboard-layout__nav" aria-label="Navegación de la tienda">
                {sections.map((section) => (
                    <div className="eshop-dashboard-layout__group" key={section.group} data-group={section.group}>
                        <div className="eshop-dashboard-layout__group-label">{section.title}</div>
                        <div className="eshop-dashboard-layout__group-items" role="list">
                            {section.items.map((item) => {
                                const accent = getAccent(item);
                                const isActive = activeNavItem?.key === item.key;
                                const Icon = item.icon;

                                const handleClick = () => {
                                    handleNavigate(item.to);
                                };

                                return (
                                    <div role="listitem" key={item.key} className="eshop-dashboard-layout__nav-item-wrapper">
                                        {renderNavButton(item.title, item.description, false, isActive, accent, handleClick, Icon)}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </nav>
        );
    };

    // Overview/Dashboard button
    const dashboardNavItem = navItemMap.get(DASHBOARD_ROUTE_KEY);
    const dashboardTitle = dashboardNavItem?.title ?? 'Panel de Tienda';
    const dashboardDescription = 'Accesos principales de la tienda.';
    const dashboardAccent = dashboardNavItem
        ? `var(--ant-color-${dashboardNavItem.colorToken}, ${token.colorPrimary})`
        : token.colorPrimary;
    const dashboardIcon = (dashboardNavItem?.icon ?? DashboardOutlined) as ComponentType;
    const dashboardIsActive =
        activeNavItem?.key === DASHBOARD_ROUTE_KEY || location.pathname.replace(/\/+$/, '') === ESHOP_HOME_PATH;

    const handleDashboardClick = () => {
        handleNavigate(ESHOP_HOME_URL);
    };

    const overviewButton: ReactNode = dashboardNavItem ? (
        <div className="eshop-dashboard-layout__overview" key={dashboardNavItem?.key ?? 'eshop:dashboard:fallback'}>
            {renderNavButton(
                dashboardTitle,
                dashboardDescription,
                false,
                dashboardIsActive,
                dashboardAccent,
                handleDashboardClick,
                dashboardIcon,
            )}
        </div>
    ) : null;

    const navHeader = (
        <div className="eshop-dashboard-layout__sidebar-header" style={mode === 'tablet' ? { position: 'relative' } : {}}>
            {!navIsCollapsed && mode === 'desktop' ? (
                <Typography.Title level={4} className="eshop-dashboard-layout__sidebar-title">
                    Panel Tienda
                </Typography.Title>
            ) : null}
            <Button
                type="text"
                size="small"
                icon={navIsCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={handleToggleCollapse}
                aria-label={navIsCollapsed ? 'Expandir menú' : 'Contraer menú'}
                className="eshop-dashboard-layout__toggle"
            />
        </div>
    );

    const navContent = (
        <div
            className="eshop-dashboard-layout__sidebar-scroll"
            style={mode === 'tablet' ? { position: 'relative', height: '100vh', paddingBottom: '5rem' } : { height: '100vh', paddingBottom: '5rem' }}
            role="presentation"
        >
            {mode !== 'mobile' ? navHeader : null}
            {overviewButton}
            {renderNavSections()}
        </div>
    );

    const sideBarNode =
        mode === 'desktop' || mode === 'tablet' ? (
            <Sider
                className="eshop-dashboard-layout__sidebar"
                data-collapsed={navIsCollapsed ? 'true' : 'false'}
                width={mode === 'desktop' && !navIsCollapsed ? 320 : 82}
                collapsedWidth={82}
                collapsed={navIsCollapsed}
                theme="light"
                style={{
                    background: 'var(--surface-section)',
                    position: 'sticky',
                    // maxHeight: '99vh',
                    maxHeight: '100vh',
                    zIndex: 1,
                }}
            >
                {navContent}
            </Sider>
        ) : null;

    return (
        <AdminLayout
            breadcrumbOptions={{
                baseNavKey: DASHBOARD_ROUTE_KEY,
                baseFallback: { key: 'eshop:dashboard:fallback', label: 'Dashboard', href: ESHOP_HOME_URL, fromRegistry: false },
                groupFilter: undefined,
            }}
            commandPaletteGroup={undefined}
            searchButtonLabel="Buscar (Ctrl+K)"
            searchButtonAriaLabel="Buscar pantallas (Ctrl+K)"
            showCart={false}
            mobileHeaderMode="compact"
            sideBar={sideBarNode}
            sideMobileMenu={mode === 'mobile'
                ? ({ openCommandPalette, searchButtonLabel, searchButtonAriaLabel }: AdminLayoutMobileMenuContext) => (
                    <>
                        <Button
                            type="text"
                            size="large"
                            icon={<MenuOutlined />}
                            onClick={handleOpenMobileNav}
                            className="eshop-dashboard-layout__mobile-trigger"
                            aria-label="Abrir menu"
                        />
                        <Drawer
                            open={mobileNavOpen}
                            onClose={handleCloseMobileNav}
                            placement="left"
                            width={320}
                            title="Panel Tienda"
                            styles={{ body: { padding: 0 } }}
                            className="eshop-dashboard-layout__drawer"
                        >
                            <div className="eshop-dashboard-layout__drawer-content">
                                <div className="eshop-dashboard-layout__drawer-tools">
                                    <Button
                                        type="default"
                                        icon={<SearchOutlined />}
                                        onClick={() => handleOpenMobileSearch(openCommandPalette)}
                                        aria-label={searchButtonAriaLabel}
                                        className="eshop-dashboard-layout__drawer-search-button"
                                        block
                                    >
                                        {searchButtonLabel}
                                    </Button>
                                </div>
                                {navContent}
                            </div>
                        </Drawer>
                    </>
                )
                : null}
        >
            <div className="eshop-dashboard-layout">
                <main className="eshop-dashboard-layout__content">
                    {mode === 'tablet' ? (
                        <Drawer
                            open={tabletOverlayOpen}
                            onClose={() => setTabletOverlayOpen(false)}
                            placement="left"
                            width={320}
                            title="Panel Tienda"
                            styles={{ body: { padding: 0 } }}
                            className="eshop-dashboard-layout__drawer"
                        >
                            <div className="eshop-dashboard-layout__drawer-content">{navContent}</div>
                        </Drawer>
                    ) : null}

                    {children}
                </main>
            </div>
        </AdminLayout>
    );
};

export default EshopDashboardLayout;
